import {
  OutletBusinessDaysController_lookup_v1,
  type OutletResource,
  OutletsController_list_v1,
  type SaleResource,
  SalesController_list_v1,
  type TerminalResource,
  TerminalsController_list_v1,
  type UserResource,
  UsersController_list_v1,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { useSession } from '@/auth/session';
import { describeRecordingGap, formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { salePath } from '../paths';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import {
  labelForPaymentMode,
  paymentModesLabel,
  SALE_PAYMENT_MODES,
  type SalePaymentMode,
  salePayments,
} from './payment-modes';
import { ReceiptLookup } from './ReceiptLookup';
import {
  businessDayIdOf,
  type DayResolution,
  formatTradingDay,
  resolutionAfterFailure,
  tradingDayHandleOf,
  tradingDayOfBusinessDay,
} from './trading-day';

/**
 * The day's sales: what the outlets took, and the filters that narrow it to the
 * question somebody is actually asking.
 *
 * ## A refunded sale is listed and marked, never dropped
 *
 * There is **no refunded filter and there must not be one** (FR-447).
 * `ListSalesQuery` says so in as many words and this screen is the other half of
 * it: a refunded sale stays in every read, marked with the moment it was
 * reversed, so a day's takings stay explicable. Money that moved twice is two
 * movements in the same day, and a sale that vanished from the list because
 * somebody reversed it is exactly the shape that makes a day inexplicable — a
 * wrong number that looks right.
 *
 * **This is deliberately the opposite of Ticketing's `includeVoided`**, which is
 * an option defaulting to *included*. Do not tidy the two into agreement. A void
 * can be asked away because a ticket's whole question is admission; a refund
 * cannot, because its question is money.
 *
 * ## The list is paged, and the visible set is the server's to decide
 *
 * `docs/ui-conventions.md`, "Reading a list", asks for the strategy to be picked
 * deliberately. This is case 2: sales grow with *use*, every day, for ever, and
 * `collect-pages.ts` names sales as the thing it is the wrong answer for.
 *
 * **The rows are rendered from the list response and nothing is fetched per
 * row.** `SaleResource` carries its `lines`, so the basket size in the Items
 * column is `lines.length` and costs no request — which is the point:
 * `SalesController.list` is asserted to cost a fixed number of statements
 * regardless of how many sales match *and* regardless of basket size, measured
 * on both axes (T021), and a client that turned one list into N detail fetches
 * would spend that guarantee. The lines themselves belong to FE033.
 *
 * The outlets, terminals and people beside it are read whole, from this same
 * file, which is the same rule applied to different sets rather than an
 * inconsistency: they fill filters that need every option at once, and the park
 * has a dozen outlets, tens of devices and tens of staff.
 *
 * ## Reach is the server's, not this screen's
 *
 * `SalesController` declares `@RequireRole()` and `@RequireOutletScope()` on all
 * three of its reads, so what a caller may see is bounded in the `WHERE` clause
 * (R-109, R-412): an owner and a manager reach every outlet, outlet and gate
 * staff reach their assignments, and a filter can only narrow within that. So
 * nothing here filters rows client-side and nothing assumes a page is the whole
 * set — the count in the rail is the server's `total`, and there is deliberately
 * **no takings sum anywhere on the screen**, because a sum of the rows on one
 * page is a number that looks like a day's takings and is not one.
 *
 * Naming an outlet outside that reach earns a `404` rather than an empty page,
 * and the three reasons — unknown, retired, out of reach — are indistinguishable
 * (FR-128). The filter is built from the outlets the API already handed back, so
 * the ordinary path never asks for one it cannot have.
 *
 * ## Two clocks, and the column says which
 *
 * Every sale carries the till's clock and the server's. "Rung up" is the till's —
 * the answer to "when did this customer pay" — and the list arrives ordered by
 * the server's, which is the only one the API trusts to order anything (FR-008,
 * FR-009). Where the two differ by more than a few minutes the gap is named
 * under the time rather than hidden: it is the account of an outage, and Day
 * Close will care. See `lib/instant.ts`.
 *
 * ## The trading day is named by date, and the server answers with the id
 *
 * `GET /v1/sales` narrows the day by **`businessDayId`**, which no picker can
 * produce — so until #228 the only way in was each row's own id as a handle.
 * That gap is closed. `GET /v1/outlets/{outletId}/business-days/{businessDate}`
 * turns an outlet and a `YYYY-MM-DD` into the day in one request, so the rail
 * carries `back-office/DayPicker.tsx` and the id on the wire is the server's
 * answer to the date somebody picked.
 *
 * **The picker needs an outlet and says so.** A business day is one row per
 * `(outlet, date)` (R-415), so a date on its own names as many days as there are
 * outlets and there is nothing to look up. Until an outlet is chosen the field
 * says that rather than offering a calendar that cannot resolve — and a row's
 * trading day sets *both*, which is why clicking one also moves the outlet
 * picker.
 *
 * **A day that will not resolve holds the list rather than widening it.** The
 * states are in `trading-day.ts`; the one worth naming here is that dropping
 * `businessDayId` while the lookup is in flight would answer for every day and
 * read exactly as though the filter had been cleared.
 *
 * **The raw id is never rendered.** It is an internal identifier, and a UUID in
 * front of somebody reconciling a till is noise they cannot act on.
 */

/** Every state the rows can be in. One value, so two cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly sales: readonly SaleResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The value a picker carries when it is not filtering. */
const ANY = 'ALL';

/**
 * The columns that are always there — receipt, rung up, payment, items, total.
 * Outlet, Cashier and Trading day are conditional and counted where they are
 * decided.
 *
 * Kept next to the header cells below, so the two cannot drift.
 */
const FIXED_COLUMNS = 5;

export function Sales() {
  const client = useApiClient();
  const { session } = useSession();

  // Two flags with the same test and the same answer today, deliberately **not**
  // folded into one. They are two different controllers, and a screen that read
  // the second off the first would go quietly wrong the day either decorator
  // moves.
  //
  // `TerminalsController` carries `@RequireRole('OWNER')` on the class, so the
  // device directory is the owner's. Without it the terminal filter has nothing
  // to offer — a sale carries a `terminalId` and no device name, and a picker of
  // bare UUIDs is not a filter.
  const mayListTerminals = session?.user.role === 'OWNER';

  // `UsersController` carries `@RequireRole('OWNER')` on every operation and
  // CL-104 is explicit that the manager role does not manage people. The staff
  // directory is what turns a sale's `actorId` into a name, so without it there
  // is no cashier filter and no Cashier column.
  //
  // **This is a gap between the ticket and the API, not a decision taken here.**
  // R-412 admits a manager to every outlet's sales, and nothing admits a manager
  // to the directory that names a person — so a manager gets the outlet and the
  // payment mode, and an owner gets those plus the two the directories carry.
  // The same gap Tickets reports. Reported in the PR body; it is an API change
  // and not something to cast around, because a UUID rendered where a name
  // belongs typechecks perfectly.
  const mayListPeople = session?.user.role === 'OWNER';

  const [outletId, setOutletId] = useState<string>(ANY);
  const [terminalId, setTerminalId] = useState<string>(ANY);
  const [actorId, setActorId] = useState<string>(ANY);
  const [paymentMode, setPaymentMode] = useState<SalePaymentMode | typeof ANY>(ANY);

  // The date somebody picked, `YYYY-MM-DD`, or `''` for every day — which is the
  // default and a real answer. **This is the only day state a person sets.** The
  // id the filter actually sends is resolved from it below and owned by that
  // effect, so there is no second value to drift out of step with this one.
  const [chosenDate, setChosenDate] = useState<string>('');

  // Where that date has got to with the server. Derived from `chosenDate` and
  // the outlet, never set from a control.
  const [resolution, setResolution] = useState<DayResolution>({ kind: 'every' });

  // The id to filter by, and `undefined` for all four states that have not got
  // one. The list effect tells "every day" apart from "not yet" before it reads
  // this, so it never sends an unfiltered query meaning a filtered one.
  const businessDayId = businessDayIdOf(resolution);

  const [offset, setOffset] = useState(0);

  const [state, setState] = useState<ListState>({ kind: 'loading' });

  // The window the last successful read answered with, kept **outside**
  // `ListState` on purpose: it survives the next load, so the pager and the
  // count stay where they are while a page is in flight instead of vanishing and
  // coming back. `null` until the first answer, because a pager reading "0
  // sales" before anything has been asked is worse than no pager.
  const [page, setPage] = useState<PageState | null>(null);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [terminals, setTerminals] = useState<readonly TerminalResource[]>([]);
  const [people, setPeople] = useState<readonly UserResource[]>([]);

  // Bumping this re-runs the load. A counter rather than a boolean, so two
  // retries in a row are two distinct values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  // Any filter moving puts the list back at the first page. Left alone, a filter
  // applied at offset 200 lands on a page past the end of a shorter result and
  // reads as "nothing matched" — `pageAfter` clamps a *step*, and this is not
  // one.
  const changeFilter = useCallback((apply: () => void) => {
    apply();
    setOffset(0);
  }, []);

  // The date somebody picked, turned into the id the list filters by.
  //
  // Keyed on the outlet as well as the date, which is what makes changing the
  // outlet **re-resolve** the same date there rather than clear it. Before #228
  // it had to clear — translating a day to another outlet meant a lookup the API
  // did not have — and clearing a filter somebody did not clear is a screen
  // answering a question they did not ask.
  useEffect(() => {
    // Nothing to resolve, and the two reasons are not the same: no date is
    // "every day", and no outlet is a date that cannot name one day. Both leave
    // the list unfiltered by day, and the rail says which of them holds.
    if (chosenDate === '' || outletId === ANY) {
      setResolution({ kind: 'every' });
      return;
    }

    const controller = new AbortController();
    let live = true;

    setResolution({ kind: 'resolving', date: chosenDate });

    client
      .request(OutletBusinessDaysController_lookup_v1, {
        // The date is passed through as the digits the calendar produced.
        // Nothing here parses it into a `Date` — `business-date.ts` owns the one
        // conversion a widget needs and says why it is local-midnight, and a
        // second one here is how a filter goes a day out.
        path: { outletId, businessDate: chosenDate },
        signal: controller.signal,
      })
      .then((day) => {
        if (live) {
          setResolution({ kind: 'resolved', date: chosenDate, day: tradingDayOfBusinessDay(day) });
        }
      })
      .catch((error: unknown) => {
        // A 404 is an answer and not a fault — that outlet did not trade that
        // day. `resolutionAfterFailure` is where the two are told apart.
        if (live) setResolution(resolutionAfterFailure(chosenDate, error));
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, chosenDate, reloads]);

  useEffect(() => {
    // Two guards, not one. `controller` stops the request itself the moment a
    // filter moves, and `live` stops a response that was already in flight from
    // being written into state after this effect was torn down — without it,
    // changing a filter twice quickly can land the first answer on top of the
    // second and leave the screen showing rows the filters exclude.
    // A day was named and the server has not answered which one it is yet, so
    // there is no question to ask. Held at loading rather than asked without the
    // filter: an unfiltered answer here would be every day's sales under a rail
    // that says one day, which is the screen lying rather than waiting.
    if (resolution.kind === 'resolving') {
      setState({ kind: 'loading' });
      return;
    }

    // And a day that resolved to nothing, or would not resolve at all, cannot be
    // asked about either. The rail says which, and the table says so in place of
    // rows — see the notices below.
    if (resolution.kind === 'absent' || resolution.kind === 'failed') return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(SalesController_list_v1, {
        query: {
          offset,
          limit: ROWS_PER_PAGE,

          // Each filter is sent only when it is filtering. The query string is
          // what appears in a log when somebody is working out what a screen
          // asked for, and a `paymentMode=ALL` in one is a filter that does not
          // exist — and `forbidNonWhitelisted` on the API would refuse it
          // outright as `VALIDATION_FAILED` rather than ignore it.
          ...(outletId === ANY ? {} : { outletId }),
          ...(terminalId === ANY ? {} : { terminalId }),
          ...(actorId === ANY ? {} : { actorId }),
          ...(paymentMode === ANY ? {} : { paymentMode }),

          // The id the lookup answered with, exactly as the server sent it.
          // Nothing here computes a day — see `trading-day.ts`. `undefined` only
          // ever means "every day" by the time this runs, because the two states
          // where it means "not yet" returned above.
          ...(businessDayId === undefined ? {} : { businessDayId }),
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (live) {
          setState({ kind: 'loaded', sales: answer.items });

          // The window the *server* answered with, not the one that was asked
          // for. They are the same today, and a pager that assumed so would be
          // silently wrong the day the API clamps a limit.
          setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
        }
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [
    client,
    outletId,
    terminalId,
    actorId,
    paymentMode,
    resolution,
    businessDayId,
    offset,
    reloads,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: a dozen outlets fill a filter that needs every option at once.
    // `GET /v1/outlets` is `@RequireRole()` + `@RequireOutletScope()`, so a
    // manager gets theirs and this list is already bounded by the same reach the
    // sales are — which is what keeps the picker from ever offering an outlet
    // whose sales would answer 404.
    //
    // A failure here is deliberately not the screen's failure — the day is still
    // readable without the outlet filter, so this leaves the list alone and the
    // filter empty rather than replacing a working screen with an error about a
    // control.
    collectPages<OutletResource>((from, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // Not asked for at all where the API would refuse it: a request that can
    // only 403 is not a request worth making, and the control it fills is not
    // rendered either. The role came out of `sessionStorage` and is editable, so
    // this is courtesy — somebody who edits it gets the refusal below, which
    // leaves the filter empty and the rest of the screen working.
    if (!mayListTerminals) {
      setTerminals([]);
      return;
    }

    const controller = new AbortController();
    let live = true;

    collectPages<TerminalResource>((from, limit) =>
      client.request(TerminalsController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setTerminals(all);
      })
      .catch(() => {
        if (live) setTerminals([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, mayListTerminals]);

  useEffect(() => {
    if (!mayListPeople) {
      setPeople([]);
      return;
    }

    const controller = new AbortController();
    let live = true;

    collectPages<UserResource>((from, limit) =>
      client.request(UsersController_list_v1, {
        query: { offset: from, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setPeople(all);
      })
      .catch(() => {
        if (live) setPeople([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, mayListPeople]);

  const outletNames = useMemo(
    () => new Map(outlets.map((outlet) => [outlet.id, outlet.name])),
    [outlets],
  );

  // Keyed by `actorId` and not by `id`. A sale names the **actor** the acting
  // staff code resolved to, which is a different identifier from the user's own
  // (FR-439) — `UserResource` carries both and `actors.id` is what history is
  // written against. Keying this by `id` would produce a table of empty cells
  // and no error anywhere.
  const personNames = useMemo(
    () => new Map(people.map((person) => [person.actorId, person.displayName])),
    [people],
  );

  const outletItems = useMemo(
    () => [
      { label: 'All outlets', value: ANY },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  // The prefix first, because it is the part printed on every receipt this
  // device issues (FR-410) and so the part somebody is reading off the paper in
  // front of them when they come to filter by it.
  const terminalItems = useMemo(
    () => [
      { label: 'All terminals', value: ANY },
      ...terminals.map((terminal) => ({
        label: `${terminal.prefix} — ${terminal.name}`,
        value: terminal.id,
      })),
    ],
    [terminals],
  );

  const personItems = useMemo(
    () => [
      { label: 'Anybody', value: ANY },
      ...people.map((person) => ({ label: person.displayName, value: person.actorId })),
    ],
    [people],
  );

  const paymentItems = useMemo(
    () => [
      { label: 'Any way', value: ANY },
      ...SALE_PAYMENT_MODES.map((mode) => ({ label: labelForPaymentMode(mode), value: mode })),
    ],
    [],
  );

  // The outlet column earns its place only when it can discriminate. Filtered to
  // one outlet it is that outlet's name repeated down the page, which is a column
  // staff learn to stop reading.
  const showOutlet = outletId === ANY;

  // And the cashier column only where there is a directory to name one from.
  const showPerson = mayListPeople;

  // The trading-day column is the *control* for a filter, so it goes once the
  // filter is on: every remaining row is that day, which would be one date
  // repeated down the page and a handle that re-applies what is already applied.
  // The chip in the rail is where the active day lives, with its clear beside
  // it.
  const showTradingDay = chosenDate === '';

  // The two day states that asked the list nothing, so the table must speak for
  // itself rather than render whatever the last answer left behind.
  const dayIsAbsent = resolution.kind === 'absent';
  const dayFailed = resolution.kind === 'failed';

  const columns =
    FIXED_COLUMNS + (showOutlet ? 1 : 0) + (showPerson ? 1 : 0) + (showTradingDay ? 1 : 0);

  const filtering =
    outletId !== ANY ||
    terminalId !== ANY ||
    actorId !== ANY ||
    paymentMode !== ANY ||
    chosenDate !== '';

  return (
    <Screen
      title="Sales"
      aside={
        <>
          {page !== null && state.kind !== 'failed' && (
            <ScreenFact label="Matching">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'sale' : 'sales'}
              {resolution.kind === 'resolved' && <> on {resolution.day.label}</>}
            </ScreenFact>
          )}

          {/* Not a form and so not `react-hook-form` — the filter rail is
              controls on a list rather than an answer somebody submits, which is
              the same shape the tickets and catalogue filters take. */}
          <div className="space-y-2">
            {outletId === ANY ? (
              // The field's own empty state, and it has to say *why* there is no
              // calendar here rather than just not drawing one: a business day is
              // one outlet's day, so a date alone names as many days as there are
              // outlets and there is nothing this screen could look up. Named by
              // what somebody must do next, not by what is missing.
              // No `htmlFor`: there is no control to point it at in this branch,
              // and a label bound to an id that is not rendered is a label that
              // names nothing.
              <>
                <Label className="text-sm text-muted-foreground">Trading day</Label>
                <p className="text-sm text-muted-foreground">Choose an outlet first.</p>
              </>
            ) : (
              <>
                {/* Bound to the trigger, which is what gives the picker its
                    accessible name: the *field* is "Trading day" and the button's
                    own text is its current value. */}
                <Label htmlFor="day-filter" className="text-sm text-muted-foreground">
                  Trading day
                </Label>

                {/* The API takes the calendar date in Asia/Kolkata that its
                    lookup path is declared with, so the value moving through
                    here is `YYYY-MM-DD` and never carries a time.
                    `business-date.ts` owns both conversions and says why they
                    are local-midnight rather than UTC — the same picker and the
                    same pair the tickets filter uses. */}
                <DayPicker
                  id="day-filter"
                  value={chosenDate}
                  format={formatBusinessDate}
                  fromDate={fromCalendarDate}
                  toDate={toCalendarDate}
                  onValueChange={(next) => {
                    changeFilter(() => setChosenDate(next));
                  }}
                />

                {/* There is deliberately **no future bound** on the calendar. It
                    would take today's date, and today is the device's opinion —
                    the one thing `lib/clock.ts` exists to keep out of behaviour.
                    A date the outlet did not trade answers "no trading day"
                    below, which is the same honest answer a future one gets. */}

                {resolution.kind === 'resolving' && (
                  <p role="status" className="text-sm text-muted-foreground">
                    Finding that day&hellip;
                  </p>
                )}

                {resolution.kind === 'absent' && (
                  // Not an error and not worded as one: the outlet genuinely has
                  // no day on that date, which is what a day it did not trade
                  // looks like. A day is opened by the first record in it and
                  // never by a read (FR-605), so there is nothing to retry.
                  <p role="status" className="text-sm text-muted-foreground">
                    No trading day on {formatBusinessDate(resolution.date)}.
                  </p>
                )}

                {resolution.kind === 'failed' && (
                  <div className="space-y-2">
                    {/* The treatment `FormMessage` gives a refusal everywhere
                        else in the app, so a message under a field looks the
                        same wherever it appears. Worded from the code and never
                        from the server's prose — see `api/failure.ts`. */}
                    <p role="status" className="text-sm text-destructive">
                      {messageForFailure(resolution.error)}
                    </p>
                    <Button variant="outline" size="sm" onClick={reload}>
                      Try again
                    </Button>
                  </div>
                )}

                {resolution.kind === 'resolved' && (
                  <div className="flex flex-wrap items-center gap-2">
                    {/* The day's state, which is a fact about the day and not a
                        filter — so a badge and never a button. A closed day may
                        be reopened (CL-603), so nothing here treats `CLOSED` as
                        final. */}
                    <Badge variant={resolution.day.state === 'CLOSED' ? 'secondary' : 'outline'}>
                      {resolution.day.state === 'CLOSED' ? 'Closed' : 'Open'}
                    </Badge>

                    {/* Without this a day closed and reopened reads exactly like
                        one never closed, and somebody reconciling a drawer
                        against these takings wants to know (CL-603). */}
                    {resolution.day.reopenCount > 0 && (
                      <span className="text-sm text-muted-foreground">
                        Reopened{' '}
                        <span className="font-mono numeric">{resolution.day.reopenCount}</span>
                        {resolution.day.reopenCount === 1 ? ' time' : ' times'}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="outlet-filter" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              // `null` is Base UI's "cleared", which these pickers have no
              // affordance for — there is no clear button and no empty option.
              // Read as "not filtering" rather than cast away, so a cleared
              // select can never become an `outletId=null` in the query string.
              onValueChange={(next) => {
                // Moving between two outlets **keeps** the date: a business day
                // belongs to one outlet — a row per `(outlet, date)` (R-415) —
                // so the day does not carry over, but the *date* does, and the
                // resolution effect looks it up at the new outlet and answers
                // either its day or "no trading day on that date". Before #228
                // this had to clear, because translating a day meant a lookup
                // the API did not have. It has one now.
                //
                // Going back to *all* outlets is the one case that still clears.
                // There is no outlet for a date to name a day at, so a date left
                // behind would be a filter that is set, inert and invisible —
                // the field would say "choose an outlet first" while the screen
                // counted itself as filtered.
                const nextOutlet = next ?? ANY;

                changeFilter(() => {
                  setOutletId(nextOutlet);

                  if (nextOutlet === ANY) setChosenDate('');
                });
              }}
            >
              <SelectTrigger
                id="outlet-filter"
                aria-label="Outlet"
                // Nothing to choose between until the outlets land, and a picker
                // with one option is a control that cannot do anything.
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ANY}>All outlets</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment-filter" className="text-sm text-muted-foreground">
              Paid by
            </Label>
            <Select
              value={paymentMode}
              items={paymentItems}
              onValueChange={(next) => {
                changeFilter(() => setPaymentMode(next ?? ANY));
              }}
            >
              <SelectTrigger
                id="payment-filter"
                aria-label="Paid by"
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ANY}>Any way</SelectItem>
                  {SALE_PAYMENT_MODES.map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {labelForPaymentMode(mode)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Rendered only where there is a device directory to fill it. Hidden
              rather than shown and refused, per docs/ui-conventions.md. */}
          {mayListTerminals && (
            <div className="space-y-2">
              <Label htmlFor="terminal-filter" className="text-sm text-muted-foreground">
                Terminal
              </Label>
              <Select
                value={terminalId}
                items={terminalItems}
                onValueChange={(next) => {
                  changeFilter(() => setTerminalId(next ?? ANY));
                }}
              >
                <SelectTrigger
                  id="terminal-filter"
                  aria-label="Terminal"
                  disabled={terminals.length === 0}
                  className="h-10 w-full bg-card"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={ANY}>All terminals</SelectItem>
                    {terminals.map((terminal) => (
                      <SelectItem key={terminal.id} value={terminal.id}>
                        {terminal.prefix} — {terminal.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          {mayListPeople && (
            <div className="space-y-2">
              <Label htmlFor="person-filter" className="text-sm text-muted-foreground">
                Cashier
              </Label>
              <Select
                value={actorId}
                items={personItems}
                onValueChange={(next) => {
                  changeFilter(() => setActorId(next ?? ANY));
                }}
              >
                <SelectTrigger
                  id="person-filter"
                  aria-label="Cashier"
                  disabled={people.length === 0}
                  className="h-10 w-full bg-card"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={ANY}>Anybody</SelectItem>
                    {people.map((person) => (
                      <SelectItem key={person.id} value={person.actorId}>
                        {person.displayName}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      }
    >
      {/* Above the list and not in the filter rail, because it is not a filter: it
          answers a different question — *this* sale, the one whose receipt is on
          the counter — and it is the question this screen exists to answer
          quickly. A customer comes back with a printed receipt and the number on
          it is all anybody has, so the field for it is the first thing on the
          screen a manager is already on rather than a search page they have to
          find first. See `ReceiptLookup.tsx`, which is also where the reason that
          number may name more than one sale is set out. */}
      <ReceiptLookup />

      <ScreenTable label="Sales">
        <TableHeader>
          <TableRow>
            <TableHead className="w-48">Receipt</TableHead>
            {/* Named for the clock it shows. Both readings exist on every sale
                and this is the till's; the server's is what the list is ordered
                by, and what a reader wants from it is the *distance*, which is
                the line under the time. See `lib/instant.ts`. */}
            <TableHead className="w-56">Rung up</TableHead>
            {/* The server's clock, as the day it landed in. Still a way into
                the filter now that the picker exists — it fills it, rather than
                carrying an id of its own. See `trading-day.ts`. */}
            {showTradingDay && <TableHead className="w-40">Trading day</TableHead>}
            {showOutlet && <TableHead>Outlet</TableHead>}
            {showPerson && <TableHead>Cashier</TableHead>}
            <TableHead className="w-28">Paid by</TableHead>
            {/* Counts right-align with the amounts beside them: both are read by
                comparing down the column. */}
            <TableHead className="w-24 text-right">Items</TableHead>
            <TableHead className="w-36 text-right">Total</TableHead>
          </TableRow>
        </TableHeader>

        {/* The day filter decides what the table can say before `state` does,
            because these two are the cases where no request was made at all.
            Ordered first so a stale set of rows from the previous day can never
            be left on screen under a rail that has moved on. */}
        {dayIsAbsent ? (
          <TableNotice columns={columns}>
            <EmptyState
              title="No trading day on that date"
              description={`${outletNames.get(outletId) ?? 'That outlet'} did not trade on ${formatBusinessDate(resolution.date)}.`}
            />
          </TableNotice>
        ) : dayFailed ? (
          <TableNotice columns={columns}>
            {/* The day could not be resolved, so the list was never asked. The
                error is the lookup's and not the list's, and the retry runs the
                lookup again. */}
            <LoadFailed error={resolution.error} onRetry={reload} />
          </TableNotice>
        ) : (
          <>
            {state.kind === 'loading' && <TableSkeleton columns={columns} />}

            {state.kind === 'failed' && (
              <TableNotice columns={columns}>
                <LoadFailed error={state.error} onRetry={reload} />
              </TableNotice>
            )}

            {state.kind === 'loaded' &&
              (state.sales.length === 0 ? (
                <TableNotice columns={columns}>
                  <EmptyState
                    title={filtering ? 'Nothing matches these filters' : 'No sales yet'}
                    description={emptyDescription(filtering)}
                  />
                </TableNotice>
              ) : (
                <TableBody>
                  {state.sales.map((sale) => (
                    <SaleRow
                      key={sale.id}
                      sale={sale}
                      outletName={
                        showOutlet ? (outletNames.get(sale.outletId) ?? UNNAMED) : undefined
                      }
                      personName={
                        showPerson ? (personNames.get(sale.actorId) ?? UNNAMED) : undefined
                      }
                      onFilterTradingDay={
                        showTradingDay
                          ? () => {
                              // Both halves, because a date alone names nothing.
                              // Moving the outlet picker too is the honest thing: the
                              // filter has narrowed to that outlet either way, and a
                              // rail that did not show it would be hiding which.
                              const handle = tradingDayHandleOf(sale);

                              changeFilter(() => {
                                setOutletId(handle.outletId);
                                setChosenDate(handle.date);
                              });
                            }
                          : undefined
                      }
                    />
                  ))}
                </TableBody>
              ))}
          </>
        )}
      </ScreenTable>

      {/* Not while the load failed: the notice above already says the list could
          not be read, and a pager under it offers to move through rows that are
          not there. `Pager` draws nothing when everything fits on one page. */}
      {page !== null && state.kind !== 'failed' && !dayIsAbsent && !dayFailed && (
        <Pager state={page} label="sales" onOffsetChange={setOffset} />
      )}
    </Screen>
  );
}

/**
 * What stands in for a name the directory could not supply.
 *
 * Reachable in one way only: the outlets, terminals or people read failed, which
 * leaves its filter empty and disabled — so the reason the cell is blank is
 * already visible in the rail beside it. An em dash and not "Unknown", which
 * would claim the server said something it did not.
 */
const UNNAMED = '—';

/**
 * What an empty table should say, which depends on why it is empty.
 *
 * Never a bare "No results". There is no "refunds are hidden" case to explain
 * here and there never will be — a refunded sale is always in the list (FR-447)
 * — so this is the simpler of the two shapes the tickets screen needs.
 *
 * There is no "create the first one" and there deliberately is not: a sale is
 * rung up at a till against a lease, never from the back office, so a call to
 * action on this screen would point at a control that cannot exist.
 */
function emptyDescription(filtering: boolean): string {
  if (filtering) {
    return 'Widen the outlet, the trading day or the payment mode.';
  }

  return 'Sales appear here as the tills sync them.';
}

/**
 * One sale, which is a **basket** summarised rather than a line.
 *
 * The total and the line count come from the list response — `SaleResource`
 * carries its `lines`, so `lines.length` is free and no row fetches anything.
 * The lines themselves are FE033's screen; showing how many there are is what
 * tells somebody scanning the day which sale was a single bottle of water and
 * which was a trolley.
 *
 * **A refunded sale is present and marked, never missing** (FR-447). It is
 * quieter than a standing one, struck through on the total and badged in the
 * receipt cell with the moment it was reversed — three marks rather than one,
 * because this is the row somebody is looking for when a day's takings do not
 * add up, and it has to be findable both by scanning down the first column and
 * by reading a single row.
 *
 * It is not coloured `destructive`. Red in this system means the system refused
 * something or is about to; a refund is an ordinary correction that already
 * happened, and money that was taken and given back is part of the record rather
 * than an error in it. The same treatment a voided ticket gets, for the same
 * reason.
 *
 * The **row carries no controls**. Refunding and reprinting are their own
 * operations and belong to FE034; this screen reads.
 */
function SaleRow({
  sale,
  outletName,
  personName,
  onFilterTradingDay,
}: {
  sale: SaleResource;

  /** Absent when the outlet column is not drawn. */
  outletName?: string | undefined;

  /** Absent when the Cashier column is not drawn. */
  personName?: string | undefined;

  /** Absent when the trading-day column is not drawn — the filter is already on. */
  onFilterTradingDay?: (() => void) | undefined;
}) {
  // A real timestamp rather than a presence check: `refundedAt` is annotated
  // `string | null` in the shipped document, unlike Ticketing's `voidedAt` whose
  // non-null branch renders as an empty object. So there is a moment here to
  // show, and it is worth showing — "refunded" without "when" sends whoever is
  // reconciling to another screen.
  const refundedAt = sale.refundedAt;
  const refunded = refundedAt !== null;

  const gap = describeRecordingGap(sale.occurredAt, sale.recordedAt);

  // `text-muted-foreground` is safe here — the row sits on `bg-card`, and it is
  // only the muted-on-muted pairing that misses AA.
  const quiet = refunded ? '' : 'text-foreground';

  return (
    <TableRow className={refunded ? 'text-muted-foreground' : undefined}>
      {/* A receipt number is read aloud off the paper and typed back in, so it
          gets the mono face and tabular figures. It carries the terminal's own
          prefix (FR-410), which is why there is no separate terminal column —
          `LK-000482` says which device rang it up. */}
      <TableCell className="whitespace-normal">
        <div className="flex flex-wrap items-center gap-2">
          {/* The number is the handle, so it is what follows through to the
              record — the same treatment the tickets list gives its own number and
              the catalogue gives an amount that leads to its price history: a
              dotted underline, not a column of blue links. Named per row, because
              "LK-000482" repeated down a list of links is no help to somebody
              navigating by them.

              The link is built from the sale's **id** and never from the number
              beside it: the number is the till's own counter and two tills can
              print the same one, so a URL built from it would name a sale by
              luck. See `salePath` and `ReceiptLookup.tsx`. */}
          <Link
            to={salePath(sale.id)}
            aria-label={`The record for sale ${sale.receiptNumber}`}
            className={`rounded-sm font-mono numeric underline decoration-border decoration-dotted underline-offset-4 outline-none hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30 ${quiet}`}
          >
            {sale.receiptNumber}
          </Link>

          {refunded && <Badge variant="outline">Refunded</Badge>}
        </div>

        {refundedAt !== null && (
          <span className="mt-1 block text-xs text-muted-foreground">
            Refunded {formatInstant(refundedAt)}
          </span>
        )}
      </TableCell>

      {/* The till's clock: when the customer actually paid. The server's clock is
          not shown as a second date — it is what the list is ordered by, and
          what a reader wants from it is the *distance*, which is the account of
          an outage. See `lib/instant.ts`. */}
      <TableCell className="whitespace-normal">
        <span className={quiet}>{formatInstant(sale.occurredAt)}</span>
        {gap !== null && <span className="mt-0.5 block text-xs text-muted-foreground">{gap}</span>}
      </TableCell>

      {onFilterTradingDay !== undefined && (
        // The handle. The same treatment the catalogue gives an amount that
        // leads to its price history and the tickets list gives a number that
        // leads to its record — a dotted underline, not a column of blue links.
        // A `button` and not a `Link`: it sets a filter on this screen rather
        // than going anywhere, and an anchor with a `preventDefault` on it is
        // worse for a keyboard and a screen reader than the button this is.
        //
        // Named per row, because one date repeated down a list of controls is no
        // help to somebody navigating by them — and the name says what it will
        // do, not what it is.
        <TableCell className="whitespace-normal">
          <button
            type="button"
            onClick={onFilterTradingDay}
            aria-label={`Show only sales from the trading day of ${formatTradingDay(sale.recordedAt)}`}
            className={`rounded-sm underline decoration-border decoration-dotted underline-offset-4 outline-none hover:decoration-current focus-visible:ring-3 focus-visible:ring-ring/30 ${quiet}`}
          >
            {formatTradingDay(sale.recordedAt)}
          </button>
        </TableCell>
      )}

      {outletName !== undefined && (
        <TableCell className={`whitespace-normal ${quiet}`}>{outletName}</TableCell>
      )}

      {personName !== undefined && (
        <TableCell className={`whitespace-normal ${quiet}`}>{personName}</TableCell>
      )}

      {/* Plain text and not a `Badge`. The conventions reserve badges for a
          status, and how something was paid for is a fact about it rather than a
          state it is in — three coloured pills repeating down every row would
          also be the "column of one repeated word" the empty-state rule warns
          about. */}
      {/* `Cash + UPI` on a split payment (#357), through the fallback. */}
      <TableCell className={quiet}>{paymentModesLabel(salePayments(sale))}</TableCell>

      {/* The basket's size, straight off the list response. Never a second
          request: the API's fixed-statement guarantee is measured on basket size
          too (T021), and a client that fetched a sale per row to count its lines
          would spend exactly what that guarantee bought. */}
      <TableCell className={`text-right font-mono numeric ${quiet}`}>{sale.lines.length}</TableCell>

      {/* Struck through when refunded, and still legible: the amount that was
          taken and given back is exactly the number somebody reconciling the day
          needs to see, so it is marked rather than blanked. `formatPaise` is the
          only thing that turns paise into an amount — nothing here divides by
          100. */}
      <TableCell className={`text-right font-mono numeric ${quiet}`}>
        <span className={refunded ? 'line-through' : undefined}>
          {formatPaise(sale.totalPaise)}
        </span>
      </TableCell>
    </TableRow>
  );
}
